/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';

import { Theme as MaterialTheme, useTheme, Stack, TextField, Button, Dialog, DialogTitle, Typography, DialogContent, InputAdornment, FormControl, InputLabel, Select, SelectChangeEvent, MenuItem } from "@mui/material";
import Grid from '@mui/material/Unstable_Grid2';
import { useState, useEffect, } from "react";

import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Cancel, Person, Save, } from '@mui/icons-material';
import { IApproverUserGuiModel } from '../../../model/IApproverGuiModel';
import ApproverBusinessLogic from '../../../businessLogic/ApproverBusinessLogic';

interface IDialogProps {
    organizationId: string,
    oldApprover: IApproverUserGuiModel,
    open: boolean,
    closeDialog: (reason: "backdropClick" | "escapeKeyDown") => void,
}

const DialogApproversReplace = (props : IDialogProps): JSX.Element => {

    const theme = useTheme();

    const { t, i18n } = useTranslation();

    const [loader, setLoader] = useState(false);

    const [newApprover, setNewApprover] = useState<IApproverUserGuiModel | undefined>(undefined);
    const [newApprovers, setNewApprovers] = useState<IApproverUserGuiModel[]>([]);

    //##########################################################################################################


    //###############################################################################################################################################
    //########################################################API Calls and useEffects###############################################################
    useEffect(() => {
        let isMounted = true; //Avoid memory leaks. Async function running after dismount.
        const logic = new ApproverBusinessLogic();

        const getData = async () => {
            if (props.organizationId) {
                setLoader(true);
                const result = await logic.getApprovers(props.organizationId);
                setLoader(false);

                if (result.hasErrors) {
                    setNewApprovers([]);
                    toast("Error: " + result.errors.join(", "));
                } else {
                    if (result.value.length > 0 && isMounted) {
                        setNewApprovers(result.value.filter(e => e.user.id !== props.oldApprover.user.id));
                    }
                }
            }
        };
        //Close current window when searching for something new
        getData();

        return () => { isMounted = false };
    }, [props.organizationId]);

    const postReplaceUser = async () => {

        if (newApprover) {
            const logic = new ApproverBusinessLogic();
            setLoader(true);
            const result = await logic.postApproverReplace(
                props.organizationId ? props.organizationId : "",
                props.oldApprover.user.id,
                newApprover.user.id);
            setLoader(false);

            if (result.hasErrors) {
                toast(t("approver.errorReplaceUser") + result.errors.join(", "));
            } else {
                props.closeDialog("escapeKeyDown");
            }
        } else {
            toast(t("approver.noUserChosen"))
        }

    }

    //########################################################################################
    //########################################################################################
    return <Dialog maxWidth="xs" fullWidth={true} onClose={(event,reason) => props.closeDialog(reason)} open={props.open}>
        <DialogTitle display="flex" justifyContent="center">
            <Typography variant="button" fontSize="1rem">{t("approver.replaceApprover")}</Typography>
        </DialogTitle>
        
        <DialogContent dividers>
            <Stack spacing={6}>
                <TextField disabled size="small" variant="outlined" color="warning" value={props.oldApprover.user.fullName ?? ""} fullWidth
                    InputProps={{
                        startAdornment:
                            <InputAdornment position="end"><Person /></InputAdornment>,
                    }}
                    id="oldApproverId" label={t("approver.oldApprover")} />
                <FormControl fullWidth size="small">
                    <InputLabel id="newApproverLabelId">{t("approver.replaceWith")}</InputLabel>
                    <Select
                        labelId="newApproverLabelId"
                        id="newApproverId"
                        value={newApprover ? newApprover.user.id : undefined}
                        label={t("approver.replaceWith")}
                        onChange={(event: SelectChangeEvent) => setNewApprover(newApprovers.find(e => e.user.id === event.target.value))}
                    >
                        {newApprovers.map(e => <MenuItem value={e.user.id}>{e ? e.user.fullName : ""}</MenuItem>)}
                    </Select>
                </FormControl>
            </Stack>
        </DialogContent>
        <DialogContent>
            <Grid container spacing={2}>
                <Grid xs={8}>
                    <Button onClick={postReplaceUser} fullWidth variant="contained" color="primary" endIcon={<Save />}>
                        {t("basic.save")}
                    </Button>
                </Grid>
                <Grid xs={4}><Button onClick={() => props.closeDialog("escapeKeyDown")} fullWidth variant="contained" color="warning" endIcon={<Cancel />}>
                    {t("basic.cancel")}
                </Button>
                </Grid>
            </Grid>
        </DialogContent>
    </Dialog>

}
export default DialogApproversReplace;